import { useEffect, useState } from 'react';
import { Container, PageHeader } from '../App';
import {
  api,
  GameInfoAuth,
  ManagementKeyRecord,
  CreatedManagementKey,
} from '../api';
import { KeyTable } from './GameSettings';

interface Props {
  user: { id: string; email: string };
  games: GameInfoAuth[];
}

type Tab = 'profile' | 'management-keys';

export default function UserSettings({ user, games }: Props) {
  const [tab, setTab] = useState<Tab>('profile');
  return (
    <Container>
      <PageHeader title="User Settings" subtitle={user.email} />
      <div className="flex gap-1 border-b border-line mb-4">
        <TabButton active={tab === 'profile'} onClick={() => setTab('profile')}>
          Profile
        </TabButton>
        <TabButton
          active={tab === 'management-keys'}
          onClick={() => setTab('management-keys')}
        >
          Management Keys
        </TabButton>
      </div>

      {tab === 'profile' && <Profile user={user} games={games} />}
      {tab === 'management-keys' && <ManagementKeysTab />}
    </Container>
  );
}

function TabButton({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      className={[
        'px-3 py-2 text-sm font-medium border-b-2 -mb-px',
        active ? 'border-fg text-fg' : 'border-transparent text-fg-muted hover:text-fg',
      ].join(' ')}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function Profile({ user, games }: Props) {
  return (
    <div className="card max-w-2xl space-y-4">
      <Field label="Email" value={user.email} />
      <Field label="User ID" value={user.id} mono />
      <Field label="Games" value={`${games.length}`} />
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs uppercase text-fg-muted font-semibold mb-1">{label}</div>
      <div className={mono ? 'text-xs font-mono break-all' : 'text-sm'}>{value}</div>
    </div>
  );
}

function SetupInstructions({ origin }: { origin: string }) {
  type Client = 'claude-http' | 'claude-stdio' | 'codex-stdio' | 'codex-http';
  const [client, setClient] = useState<Client>('claude-http');

  const httpUrl = `${origin}/api/v1/mcp`;
  const stdioBin = 'forge-mcp'; // assumes binary on PATH; otherwise: cargo run -p forge-service --bin forge-mcp

  const snippets: Record<Client, { label: string; lang: string; body: string; note?: string }> = {
    'claude-http': {
      label: 'Claude Code (HTTP)',
      lang: 'sh',
      body:
`claude mcp add forge \\
  --transport http \\
  --url "${httpUrl}" \\
  --header "Authorization: Bearer $FORGE_MGMT_KEY"`,
      note: 'Replace $FORGE_MGMT_KEY with the plaintext token shown when you create a key below.',
    },
    'claude-stdio': {
      label: 'Claude Code (stdio)',
      lang: 'sh',
      body:
`FORGE_MGMT_KEY=gfm_... claude mcp add forge -- ${stdioBin}`,
      note: 'Requires the forge-mcp binary on PATH. Install with: cargo install --path crates/forge-service --bin forge-mcp',
    },
    'codex-http': {
      label: 'Codex (HTTP, ~/.codex/config.toml)',
      lang: 'toml',
      body:
`[mcp_servers.forge]
url = "${httpUrl}"
transport = "http"
headers = { Authorization = "Bearer \${FORGE_MGMT_KEY}" }`,
      note: 'Set FORGE_MGMT_KEY in your shell or in Codex’s env block.',
    },
    'codex-stdio': {
      label: 'Codex (stdio, ~/.codex/config.toml)',
      lang: 'toml',
      body:
`[mcp_servers.forge]
command = "${stdioBin}"
env = { FORGE_MGMT_KEY = "gfm_..." }`,
      note: 'Replace gfm_... with the plaintext token from a key created below.',
    },
  };

  const active = snippets[client];

  return (
    <div className="card mb-4">
      <div className="font-medium mb-2">Connect an AI assistant</div>
      <div className="text-xs text-fg-muted mb-3">
        Pick your client + transport. Copy the snippet, replacing the management-key placeholder
        with a token you create below (plaintext is shown once at creation).
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(Object.keys(snippets) as Client[]).map(c => (
          <button
            key={c}
            type="button"
            className={[
              'px-2.5 py-1 text-xs rounded-md border',
              c === client
                ? 'border-fg bg-bg-2 text-fg'
                : 'border-line text-fg-muted hover:text-fg',
            ].join(' ')}
            onClick={() => setClient(c)}
          >
            {snippets[c].label}
          </button>
        ))}
      </div>
      <pre className="bg-bg-2 border border-line rounded px-3 py-2 text-xs overflow-x-auto whitespace-pre">
        <code>{active.body}</code>
      </pre>
      <div className="flex items-center gap-2 mt-2">
        <button
          type="button"
          className="btn btn-ghost text-xs"
          onClick={() => navigator.clipboard?.writeText(active.body)}
        >
          Copy snippet
        </button>
        {active.note && (
          <div className="text-[11px] text-fg-muted">{active.note}</div>
        )}
      </div>
      <div className="text-[11px] text-fg-muted mt-3">
        Required scopes for the full toolset: <code>packages:read</code>,{' '}
        <code>packages:write</code>, <code>publish</code>. Newly created keys get all three by
        default; downscope by hitting <code>POST /api/v1/auth/management-keys</code> directly.
      </div>
    </div>
  );
}

function ManagementKeysTab() {
  const [keys, setKeys] = useState<ManagementKeyRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [justCreated, setJustCreated] = useState<CreatedManagementKey | null>(null);

  const reload = () => {
    api.listManagementKeys()
      .then(res => setKeys(res.keys))
      .catch(e => setError((e as Error).message));
  };
  useEffect(reload, []);

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true); setError(null);
    try {
      const created = await api.createManagementKey({
        name: newName.trim(),
        scopes: ['packages:read', 'packages:write', 'publish'],
      });
      setJustCreated(created);
      setNewName('');
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const revoke = async (keyId: string) => {
    if (!confirm('Revoke this management key?')) return;
    try {
      await api.revokeManagementKey(keyId);
      reload();
    } catch (e) { setError((e as Error).message); }
  };

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://your-forge-host';
  return (
    <div>
      <div className="text-xs text-fg-muted mb-3">
        Used by MCP clients (Claude Code, Codex) and admin scripts that manage packages on your behalf across all of your games.
      </div>

      <SetupInstructions origin={origin} />

      {error && <div className="card border-red-500/40 mb-4 text-red-500">{error}</div>}

      {justCreated && (
        <div className="card mb-4 border-green-500/40">
          <div className="font-medium mb-1">Management key created — copy it now</div>
          <div className="text-xs text-fg-muted mb-2">
            Use as <code>Bearer</code> on <code>/api/v1/mcp</code>, or as <code>FORGE_MGMT_KEY</code> for the <code>forge-mcp</code> stdio binary.
          </div>
          <code className="block break-all bg-bg-2 border border-line rounded px-2 py-1.5 text-xs">
            {justCreated.token}
          </code>
          <button className="btn btn-ghost text-xs mt-2" onClick={() => setJustCreated(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div className="card mb-4">
        <div className="font-medium mb-2">Create new management key</div>
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder="Key name (e.g. claude-code-laptop)"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            disabled={busy}
          />
          <button
            className="btn btn-primary"
            onClick={create}
            disabled={busy || !newName.trim()}
          >
            {busy ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="font-medium mb-3">Active management keys</div>
        {keys.length === 0 ? (
          <div className="empty text-sm">No management keys yet.</div>
        ) : (
          <KeyTable rows={keys} onRevoke={revoke} />
        )}
      </div>
    </div>
  );
}
